// Runtime audio — loads the bundled clips and plays them via Web Audio so we can
// sequence sounds for blending and fire a callback as each one starts (to highlight
// the matching sound-box). If a clip is missing (audio not generated yet), playback
// is a silent no-op so the UI still works.

export interface AudioClip {
  id: string;
  file: string;
  kind: string;
  durMs: number;
}

let ctx: AudioContext | undefined;
const buffers = new Map<string, AudioBuffer>();
const clips = new Map<string, AudioClip>();
let ready: Promise<void> | undefined;

function audioCtx(): AudioContext {
  if (!ctx) {
    const Ctor = window.AudioContext ?? (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext;
    ctx = new Ctor();
  }
  return ctx;
}

/** Fetch + decode every clip once. Safe to call repeatedly. */
export function loadAudio(): Promise<void> {
  if (ready) return ready;
  ready = (async () => {
    const base = import.meta.env.BASE_URL;
    let list: AudioClip[] = [];
    try {
      const res = await fetch(`${base}audio/manifest.json`);
      if (res.ok) list = await res.json();
    } catch {
      console.warn('[audio] no manifest yet — running silent');
      return;
    }
    const c = audioCtx();
    await Promise.all(
      list.map(async (clip) => {
        clips.set(clip.id, clip);
        try {
          const r = await fetch(`${base}audio/${clip.file}`);
          const ab = await r.arrayBuffer();
          buffers.set(clip.id, await c.decodeAudioData(ab));
        } catch (e) {
          console.warn('[audio] failed to load', clip.id, e);
        }
      }),
    );
  })();
  return ready;
}

export function hasClip(id: string): boolean {
  return buffers.has(id);
}

export function clipDurMs(id: string): number {
  return clips.get(id)?.durMs ?? 0;
}

/** A short, synthesized success chime for reward juice (no audio asset needed, fully
 *  offline). Used on the reward screen / mission-complete where no voice is speaking, so
 *  it never overlaps a coaching line. */
export function playEarcon(kind: 'build' | 'coin' = 'build'): void {
  try {
    const c = audioCtx();
    if (c.state === 'suspended') void c.resume();
    const now = c.currentTime;
    const notes = kind === 'coin' ? [660, 990] : [523.25, 659.25, 783.99]; // bright major arpeggio
    notes.forEach((freq, i) => {
      const osc = c.createOscillator();
      const g = c.createGain();
      osc.type = 'triangle';
      osc.frequency.value = freq;
      const t = now + i * 0.09;
      g.gain.setValueAtTime(0.0001, t);
      g.gain.exponentialRampToValueAtTime(0.25, t + 0.02);
      g.gain.exponentialRampToValueAtTime(0.0001, t + 0.3);
      osc.connect(g).connect(c.destination);
      osc.start(t);
      osc.stop(t + 0.32);
    });
  } catch {
    // audio unavailable — silent, UI still works
  }
}

// Single audio channel: only one thing plays at a time. `gen` is bumped whenever
// playback is superseded, so an in-flight sequence cancels cleanly when new audio
// starts (no overlapping voices — e.g. a coach line over word-by-word reading).
let activeSource: AudioBufferSourceNode | null = null;
let gen = 0;

/** Stop whatever is playing and invalidate any running sequence. */
export function stopAudio(): void {
  gen += 1;
  if (activeSource) {
    try {
      activeSource.stop(); // fires onended, which settles the awaiting play()
    } catch {
      /* already stopped */
    }
    activeSource = null;
  }
}

async function resume(): Promise<void> {
  const c = audioCtx();
  if (c.state === 'suspended') {
    try {
      await c.resume();
    } catch {
      /* will retry on next gesture */
    }
  }
}

/** Play one clip under a generation token; resolves when it ends or is superseded. */
function playBuffer(id: string, myGen: number): Promise<void> {
  const buf = buffers.get(id);
  if (!buf) return Promise.resolve();
  return resume().then(
    () =>
      new Promise<void>((resolve) => {
        if (myGen !== gen) return resolve(); // superseded while resuming
        const c = audioCtx();
        const src = c.createBufferSource();
        src.buffer = buf;
        src.connect(c.destination);
        activeSource = src;
        src.onended = () => {
          if (activeSource === src) activeSource = null;
          resolve();
        };
        src.start();
      }),
  );
}

/** Play one clip; interrupts anything already playing. Resolves when it ends. */
export function play(id: string): Promise<void> {
  stopAudio();
  return playBuffer(id, gen);
}

export interface SequenceOpts {
  /** Pause between clips (ms). */
  gapMs?: number;
  onClipStart?: (index: number, id: string) => void;
  onClipEnd?: (index: number, id: string) => void;
}

/**
 * Play clips back-to-back (e.g. reading a beat word-by-word). Interrupts anything
 * already playing; if a newer play()/playSequence() starts, this one bails cleanly.
 */
export async function playSequence(ids: string[], opts: SequenceOpts = {}): Promise<void> {
  stopAudio();
  const myGen = gen;
  const gap = opts.gapMs ?? 120;
  for (let i = 0; i < ids.length; i++) {
    if (myGen !== gen) return;
    opts.onClipStart?.(i, ids[i]);
    await playBuffer(ids[i], myGen);
    if (myGen !== gen) return;
    opts.onClipEnd?.(i, ids[i]);
    if (i < ids.length - 1 && gap > 0) await sleep(gap);
  }
}

function sleep(ms: number): Promise<void> {
  return new Promise((r) => setTimeout(r, ms));
}
